import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { MeDto } from '@koverta/shared';
import { Flag, LogoMark, Seg } from '../components/ui';
import { errorMessage, post } from '../lib/api';
import { queryClient } from '../lib/queries';

/** First-run household setup (M0): base currency, number locale, start of week. */
export function SetupPage({ me }: { me: MeDto }) {
  const [name, setName] = useState('Naše domaćinstvo');
  const [displayName, setDisplayName] = useState(me.user.name.split(' ')[0] ?? '');
  const [base, setBase] = useState<'RSD' | 'EUR'>('RSD');
  const [locale, setLocale] = useState<'sr-Latn' | 'en'>('sr-Latn');
  const [weekStart, setWeekStart] = useState<'mon' | 'sun'>('mon');
  const [startWith, setStartWith] = useState<'starter' | 'ynab'>('starter');
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await post('/api/household/setup', {
        name,
        displayName,
        baseCurrency: base,
        numberLocale: locale,
        weekStart,
        // A YNAB import brings its own categories (ynab-import.md §3).
        starterCategories: startWith === 'starter',
      });
      if (startWith === 'ynab') navigate('/settings/ynab?start=1', { replace: true });
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (x) {
      setErr(errorMessage(x));
      setBusy(false);
    }
  };
  return (
    <div className="frame auth">
      <form className="auth-card wizard" onSubmit={submit}>
        <span className="logo" style={{ width: 44, height: 44, borderRadius: 13 }}>
          <LogoMark />
        </span>
        <h1 style={{ margin: '6px 0 0' }}>Set up your budget</h1>
        <p style={{ margin: 0 }}>You can invite your partner from Settings › Household afterwards.</p>
        <label className="field">
          Household name
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={80}
          />
        </label>
        <label className="field">
          Your name
          <input
            className="input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            maxLength={40}
          />
        </label>
        <div className="field">
          Base currency · budget, targets and reports use it. Locked after the first transaction.
          <Seg
            value={base}
            onChange={setBase}
            label="Base currency"
            options={[
              [
                'RSD',
                <>
                  <Flag currency="RSD" /> RSD
                </>,
              ],
              [
                'EUR',
                <>
                  <Flag currency="EUR" /> EUR
                </>,
              ],
            ]}
          />
        </div>
        <div className="field">
          Number format
          <Seg
            value={locale}
            onChange={setLocale}
            label="Number format"
            options={[
              ['sr-Latn', 'Serbian · 12.450,00'],
              ['en', 'English · 12,450.00'],
            ]}
          />
        </div>
        <div className="field">
          Start of week
          <Seg
            value={weekStart}
            onChange={setWeekStart}
            label="Start of week"
            options={[
              ['mon', 'Monday'],
              ['sun', 'Sunday'],
            ]}
          />
        </div>
        <div className="field">
          Start with
          <Seg
            value={startWith}
            onChange={setStartWith}
            label="Start with"
            options={[
              ['starter', 'Starter categories'],
              ['ynab', 'Import from YNAB'],
            ]}
          />
          {startWith === 'ynab' ? (
            <span className="muted" style={{ fontSize: 12.5 }}>
              Your accounts, categories and history come from YNAB next. The base currency is taken from the
              YNAB budget.
            </span>
          ) : null}
        </div>
        {err ? <div className="err-t">{err}</div> : null}
        <button className="btn primary big" type="submit" disabled={busy}>
          Create budget
        </button>
      </form>
    </div>
  );
}
